export type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'datetime' | 'boolean' | 'pipeline' | 'stage';

export interface FieldDef {
  key: string;
  label: string;
  type?: FieldType;
}

export interface ColumnDef {
  key: string;
  label: string;
}

export interface ObjectDef {
  key: string;
  path: string;
  label: string;
  columns: ColumnDef[];
  fields: FieldDef[];
  /** Fields offered in the filter bar. */
  filterFields: { key: string; label: string }[];
  /** If set, this object is only shown/usable when the tenant has this feature module. */
  module?: string;
}

export const OBJECTS: ObjectDef[] = [
  {
    key: 'account', path: 'accounts', label: 'Accounts',
    columns: [{ key: 'name', label: 'Name' }, { key: 'industry', label: 'Industry' }, { key: 'region', label: 'Region' }, { key: 'status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Name' }, { key: 'accountType', label: 'Type' }, { key: 'industry', label: 'Industry' },
      { key: 'region', label: 'Region' }, { key: 'country', label: 'Country' }, { key: 'website', label: 'Website' },
      { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' }, { key: 'status', label: 'Status' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
    filterFields: [{ key: 'name', label: 'Name' }, { key: 'industry', label: 'Industry' }, { key: 'region', label: 'Region' }, { key: 'status', label: 'Status' }],
  },
  {
    key: 'contact', path: 'contacts', label: 'Contacts',
    columns: [{ key: 'fullName', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'status', label: 'Status' }, { key: 'region', label: 'Region' }],
    fields: [
      { key: 'firstName', label: 'First name' }, { key: 'lastName', label: 'Last name' }, { key: 'email', label: 'Email' },
      { key: 'workPhone', label: 'Work phone' }, { key: 'mobilePhone', label: 'Mobile' }, { key: 'title', label: 'Title' },
      { key: 'status', label: 'Status' }, { key: 'lifecycleStage', label: 'Lifecycle stage' }, { key: 'region', label: 'Region' },
      { key: 'accountId', label: 'Account ID' },
    ],
    filterFields: [{ key: 'fullName', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'status', label: 'Status' }, { key: 'region', label: 'Region' }],
  },
  {
    key: 'lead', path: 'leads', label: 'Leads', module: 'leads',
    columns: [{ key: 'fullName', label: 'Name' }, { key: 'company', label: 'Company' }, { key: 'email', label: 'Email' }, { key: 'status', label: 'Status' }, { key: 'source', label: 'Source' }],
    fields: [
      { key: 'firstName', label: 'First name' }, { key: 'lastName', label: 'Last name' }, { key: 'company', label: 'Company' },
      { key: 'title', label: 'Title' }, { key: 'email', label: 'Email' }, { key: 'workPhone', label: 'Work phone' },
      { key: 'mobilePhone', label: 'Mobile' }, { key: 'status', label: 'Status' }, { key: 'source', label: 'Source' },
      { key: 'rating', label: 'Rating' }, { key: 'score', label: 'Score', type: 'number' }, { key: 'industry', label: 'Industry' },
      { key: 'region', label: 'Region' }, { key: 'linkedinUrl', label: 'LinkedIn' }, { key: 'description', label: 'Description', type: 'textarea' },
    ],
    filterFields: [{ key: 'fullName', label: 'Name' }, { key: 'company', label: 'Company' }, { key: 'email', label: 'Email' }, { key: 'status', label: 'Status' }, { key: 'source', label: 'Source' }, { key: 'region', label: 'Region' }],
  },
  {
    key: 'deal', path: 'deals', label: 'Deals',
    columns: [{ key: 'name', label: 'Name' }, { key: 'amount', label: 'Amount' }, { key: 'status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Name' }, { key: 'amount', label: 'Amount', type: 'number' }, { key: 'currency', label: 'Currency' },
      { key: 'status', label: 'Status' }, { key: 'expectedCloseDate', label: 'Expected close', type: 'date' },
      { key: 'pipelineId', label: 'Pipeline', type: 'pipeline' }, { key: 'stageId', label: 'Stage', type: 'stage' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
    filterFields: [{ key: 'name', label: 'Name' }, { key: 'status', label: 'Status' }, { key: 'amount', label: 'Amount' }],
  },
  {
    key: 'task', path: 'tasks', label: 'Tasks',
    columns: [{ key: 'title', label: 'Title' }, { key: 'priority', label: 'Priority' }, { key: 'status', label: 'Status' }, { key: 'dueDate', label: 'Due' }],
    fields: [
      { key: 'title', label: 'Title' }, { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'priority', label: 'Priority' }, { key: 'status', label: 'Status' }, { key: 'dueDate', label: 'Due date', type: 'datetime' },
    ],
    filterFields: [{ key: 'title', label: 'Title' }, { key: 'priority', label: 'Priority' }, { key: 'status', label: 'Status' }],
  },
  {
    key: 'meeting', path: 'meetings', label: 'Meetings',
    columns: [{ key: 'title', label: 'Title' }, { key: 'startAt', label: 'Start' }, { key: 'status', label: 'Status' }],
    fields: [
      { key: 'title', label: 'Title' }, { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'location', label: 'Location' }, { key: 'startAt', label: 'Start', type: 'datetime' },
      { key: 'endAt', label: 'End', type: 'datetime' }, { key: 'status', label: 'Status' },
    ],
    filterFields: [{ key: 'title', label: 'Title' }, { key: 'status', label: 'Status' }],
  },
  {
    key: 'product', path: 'products', label: 'Products',
    columns: [{ key: 'name', label: 'Name' }, { key: 'sku', label: 'SKU' }, { key: 'unitPrice', label: 'Price' }, { key: 'status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Name' }, { key: 'sku', label: 'SKU' }, { key: 'category', label: 'Category' },
      { key: 'unitPrice', label: 'Unit price', type: 'number' }, { key: 'currency', label: 'Currency' },
      { key: 'status', label: 'Status' }, { key: 'description', label: 'Description', type: 'textarea' },
    ],
    filterFields: [{ key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'status', label: 'Status' }],
  },
  {
    key: 'solution', path: 'solutions', label: 'Solutions',
    columns: [{ key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'status', label: 'Status' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
    filterFields: [{ key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'status', label: 'Status' }],
  },
];

export const OPERATORS = [
  { value: 'contains', label: 'contains' },
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
];
